import { AxiosResponse } from "axios";
import axiosCustom from "@/utils/axiosCustom";
import { Cart } from "@/types/Cart";
import { CartItem, CartItemPaymentTerms } from "@/types/cart-item";
import store from "@/store/index";
import ProductService from "@/services/ProductService";
import IProduct from "@/models/protocols/IProduct";
import { Defaults } from "@/types/Defaults";
import IAddress from "@/models/protocols/IAddress";
import countryService from "./CountryService";

export default class CartService {
  /**
   * Add cart items to remote cart
   * @param cart
   */
  static async addCartToRemote(cart: Cart): Promise<string> {
    const cartId = await this.toRemote(cart);
    await this.setMailingAddress(cartId);
    return cartId;
  }

  private static async setMailingAddress(cartId: any): Promise<void> {
    const user = (store.state as any).Root.user;
    if (!user.addresses || !user.addresses.length) return;
    const addr = user.addresses[0];
    try {
      await axiosCustom.post("/rest/V1/wag/checkout/mailing-address", {
        firstname: addr.firstname,
        lastname: addr.lastname,
        street: addr.street.length ? addr.street[0] : "",
        street2: addr.street.length > 1 ? addr.street[1] : "",
        city: addr.city,
        regionCode: addr.region.regionCode,
        regionId: addr.region.regionId,
        region: addr.region.region,
        postcode: addr.postcode,
        telephone: addr.telephone,
        quoteId: parseInt(cartId, 10) || 0,
      });
    } catch (e) {
      console.warn("setMailingAddress failed (non-fatal):", e);
    }
  }

  /**
   * Issues policy with the items in the cart
   * @param cart
   * @param cartId
   */
  static async issuePolicy(cart: Cart, cartId: any): Promise<any> {
    // Get the user from store, if any
    const user = (store.state as any).Root.user;

    // if (!user.addresses.length) {
    //   throw new Error("User has no address defined.");
    // }

    // const address: IAddress = user.addresses[0];

    const body = {
      cartId: cartId,
      billingAddress: {
        customerAddressId: cart.billing_info.address_book_id,
        countryId: cart.billing_info.countryId,
        regionId: cart.billing_info.region.regionId,
        regionCode: cart.billing_info.region
          ? cart.billing_info.region.regionCode
          : "",
        region: cart.billing_info.region ? cart.billing_info.region.region : "",
        customerId: user.id,
        street: [cart.billing_info.address ? cart.billing_info.address : ""],
        company: null,
        telephone: cart.billing_info.telephone.replace(/\D/g, ""),
        fax: null,
        postcode: cart.billing_info.postcode,
        city: cart.billing_info.city,
        firstname: cart.billing_info.firstname,
        lastname: cart.billing_info.lastname,
        middlename: null,
        prefix: null,
        suffix: null,
        vatId: null,
        customAttributes: [],
        saveInAddressBook: null,
      },
      paymentMethod: {
        method: "wise_payment",
        additional_data: {
          cc_cid: cart.billing_info.cvc,
          cc_ss_start_month: "",
          cc_ss_start_year: "",
          cc_ss_issue: "",
          cc_type: cart.billing_info.cardTypeShort,
          cc_exp_year: cart.billing_info.expirationYear,
          cc_exp_month: cart.billing_info.expirationMonth,
          cc_number: cart.billing_info.card_number,
          captcha_string: null,
          mailing_firstname: user.addresses.length
            ? user.addresses[0].firstname
            : cart.billing_info.firstname,
          mailing_lastname: user.addresses.length
            ? user.addresses[0].lastname
            : cart.billing_info.lastname,
          mailing_address_1:
            user.addresses.length && user.addresses[0].street.length
              ? user.addresses[0].street[0]
              : cart.billing_info.address,
          mailing_address_2:
            user.addresses.length && user.addresses[0].street.length > 1
              ? user.addresses[0].street[1]
              : "",
          mailing_city: user.addresses.length
            ? user.addresses[0].city
            : cart.billing_info.city,
          mailing_region_code: user.addresses.length
            ? user.addresses[0].region.regionCode
            : cart.billing_info.region.regionCode,
          mailing_postcode: user.addresses.length
            ? user.addresses[0].postcode
            : cart.billing_info.postcode,
        },
        extension_attributes: {
          agreement_ids: [cart.terms_agree ? "1" : "0"],
        },
      },
    };
    console.log("payment body", body);
    const { data } = await axiosCustom.post(
      "/rest/portal/V1/carts/mine/payment-information",
      body
    );
    return data;
  }
  /**
   * Add all items from local cart to remote.
   * @param cart
   */
  private static async toRemote(cart: Cart): Promise<any> {
    // Get the user from store, if any
    const user = (store.state as any).Root.user;
    const remoteCart = await this.getCart();
    let id = "";
    // First delete any existing cart items on remote side
    if (remoteCart) {
      id = remoteCart.id;
      await this.deleteRemoteCartItems();
    } else {
      const response = await axiosCustom.post("/rest/portal/V1/carts/mine", {});
      // this id is the quote id
      id = response.data;
    }

    // Ok, now add cart items
    await Promise.all(
      cart.items.map(async (item) => {
        await this.addCartItem(item, cart, id);
      })
    );

    return id;
  }

  /**
   * Delete cart items from remote.
   */
  private static async deleteRemoteCartItems(): Promise<void> {
    await axiosCustom.delete("/rest/portal/V1/WAG/carts/mine/delete-all");
  }

  //TODO check refactor
  private static async addCartItem(
    item: CartItem,
    cart: Cart,
    quote_id: string
  ): Promise<void> {
    // First, get the product for this cart item
    const products = await ProductService.all();
    console.log("PP", products);
    const product = products.find((p) => {
      if (item.name.toLowerCase().includes("watch")) {
        return p.name?.includes(item.name);
      } else {
        return item.type.toString() == p.property_type_id;
      }
    });

    // Configure product options
    const itemOptions = this.configureCustomizableOptions(item, cart, product);

    const body = {
      cartItem: {
        sku: product ? product.main_sku : "",
        qty: 1,
        quote_id: quote_id,
        product_option: {
          extension_attributes: {
            custom_options: itemOptions,
          },
        },
        extension_attributes: {},
      },
    };
    await axiosCustom.post("/rest/portal/V1/carts/mine/items", body);
  }

  public static async saveQuote(
    cart: Cart,
    customerId: number,
    utm_source: string | null | undefined = "",
    utm_medium: string | null | undefined = "",
    utm_campaign: string | null | undefined = "",
    utm_content: string | null | undefined = "",
    quoteId: string | null | undefined = ""
  ): Promise<number> {
    const cartItem: any[] = [];
    console.log("CART", cart);
    const products = await ProductService.all();
    cart.items.forEach((item: CartItem) => {
      const product = products.find((prod) => prod.name === item.name);
      cartItem.push({
        name: item.name,
        coverage: item.coverage,
        deductible: item.deductible,
        policy_term: product
          ? product.policy_terms?.find(
              (policy_term) => policy_term.value === item.policy_term
            )?.text
          : undefined,
        payment_term: item.policyTermNameNew,
        manufacturer: item.manufacturer,
        model: item.model,
        serial_number: item.serial_number,
        sku: item.sku,
        price: item.due_today,
      });
    });
    const stateTitle = await countryService.getTitleByCode(cart.stateValue);
    console.log("STATE", stateTitle);
    const body = {
      customer_id: customerId,
      utm_source: utm_source,
      utm_medium: utm_medium,
      utm_campaign: utm_campaign,
      utm_content: utm_content,
      items: cartItem,
      state: stateTitle,
    };

    const result = await axiosCustom.post("/rest/V1/WAG/quote", body);
    console.log("Quote ID from API:", result.data);
    return result.data;
  }

  /**
   * Update quote with order id
   * @param quoteId
   * @param orderId
   * @returns
   */
  public static async updateQuote(
    quoteId: number,
    orderId: number
  ): Promise<number> {
    const body = {
      order_id: orderId,
    };

    try {
      const result = await axiosCustom.put(
        `/rest/V1/WAG/quote/${quoteId}`,
        body
      );
      return result.data;
    } catch (error) {
      console.error("Error updating quote with order id", error);
      return 0;
    }
  }

  /**
   * Gets current cart or null if none
   */
  private static async getCart(): Promise<any> {
    try {
      // Get cart information
      const cartInfoResp = await axiosCustom.get("/rest/portal/V1/carts/mine");
      return cartInfoResp.data;
    } catch (error) {
      return null;
    }
  }

  private static configureCustomizableOptions(
    item: CartItem,
    cart: Cart,
    product?: IProduct
  ): any[] {
    const itemOptions: any[] = [];
    if (!product || !product.customizable_options) {
      throw new Error("Product cannot be found.");
    }
    product.customizable_options.forEach((option) => {
      switch (option.title) {
        case "price":
          itemOptions.push({
            option_id: option.id,
            option_value: item.due_today, //51.41 // TODO: Check if this value should be here? Should this be formatted toCurrency?
          });
          break;
        case "sku":
          itemOptions.push({
            option_id: option.id,
            //option_value: item.sku, //undefined
            option_value: product.main_sku,
          });
          break;
        case "Product Name":
          itemOptions.push({
            option_id: option.id,
            option_value: product.name,
          });
          break;
        case "Make":
          itemOptions.push({
            option_id: option.id,
            option_value: item.manufacturer, //Apple
          });
          break;
        case "Model":
          itemOptions.push({
            option_id: option.id,
            option_value: item.model, //12
          });
          break;
        case "Serial":
          itemOptions.push({
            option_id: option.id,
            option_value: item.serial_number, //"2321312312"
          });
          break;
        case "Product Code":
          itemOptions.push({
            option_id: option.id,
            option_value: product.product_code,
          });
          break;
        case "Promocode":
          itemOptions.push({
            option_id: option.id,
            option_value: product.promo_code,
          });
          break;
        case "Portal Id":
          // TODO: check what to do with portal id
          break;
        case "State":
          itemOptions.push({
            option_id: option.id,
            option_value: cart.stateValue,
          });
          break;
        case "User Type":
          itemOptions.push({
            option_id: option.id,
            option_value: cart.stateValue,
          });
          break;
        case "Deductible":
          itemOptions.push({
            option_id: option.id,
            option_value: item.deductible,
          });
          break;
        case "Coverage Amount":
          itemOptions.push({
            option_id: option.id,
            option_value: item.coverage,
          });
          break;
        case "Coverage Type Value":
          itemOptions.push({
            option_id: option.id,
            option_value: item.coverage_type,
          });
          break;
        case "Coverage Type":
          itemOptions.push({
            option_id: option.id,
            option_value: product.coverage_types[0].text,
          });
          break;
        case "Policy Term":
          itemOptions.push({
            option_id: option.id,
            option_value: product
              ? product.policy_terms?.find(
                  (policy_term) => policy_term.value === item.policy_term
                )?.text
              : undefined, //1,2,3
          });
          break;
        case "Policy Term Value":
          itemOptions.push({
            option_id: option.id,
            option_value: product
              ? product.policy_terms?.find(
                  (policy_term) => policy_term.value === item.policy_term
                )?.value
              : undefined, //1,2,3
          });
          break;
        case "Payment Term Value":
          itemOptions.push({
            option_id: option.id,
            option_value: item.policyTermNameNew, //monthly = 1, yearly-renew = 3, yearly = 2
          });
          break;
        case "Payment Term":
          itemOptions.push({
            option_id: option.id,
            option_value: item.policyTermName,
          });
          break;
        case "Is Monthly":
          itemOptions.push({
            option_id: option.id,
            option_value:
              item.payment_terms == CartItemPaymentTerms.Monthly
                ? "true"
                : "false",
          });
          break;
        case "Installment Premium":
          itemOptions.push({
            option_id: option.id,
            option_value: item.installmentPremium,
          });
          break;
        case "Fee Sku":
          itemOptions.push({
            option_id: option.id,
            option_value: item.processing_fee_sku,
          });
          break;
        case "Fee":
          itemOptions.push({
            option_id: option.id,
            option_value: item.processing_fee,
          });
          break;
        case "Portal Img Url":
          //
          break;
        case "Portal Logo Url":
          //
          break;
        case "School Key":
          //
          break;
        case "School":
          //
          break;
        case "City":
          itemOptions.push({
            option_id: option.id,
            option_value: cart.billing_info.city,
          });
          break;
        case "First Name":
          //
          break;
        case "Last Name":
          //
          break;
        case "Product Property Type Id":
          itemOptions.push({
            option_id: option.id,
            option_value: product.product_property_type_id,
          });
          break;
        case "Deductible Text":
          itemOptions.push({
            option_id: option.id,
            option_value: this.getDeductibleText(
              product.deductibles,
              item.deductible
            ),
          });
          break;
        // case "Quote Id":
        //   itemOptions.push({
        //     option_id: option.id,
        //     option_value: item.quoteId,
        //   });
        //   break;
      }
    });
    console.log("Item Options", itemOptions);
    return itemOptions;
  }
  private static getDeductibleText(deductibles, value) {
    const item = deductibles.find((obj) => obj.value === value);
    return item ? item.text : null;
  }
}
